import React, { useState } from "react";
import { motion } from "framer-motion";

const PaymentHistory = ({ userData }) => {
    const [searchTerm, setSearchTerm] = useState("");
    const [filterType, setFilterType] = useState("all");
    const [filterStatus, setFilterStatus] = useState("all");

    // Mock payment data for law firm
    const payments = [
        {
            id: 1,
            type: "client",
            description: "John Smith - I-130 Petition",
            amount: 2500,
            status: "completed",
            date: "2024-01-20",
            paymentMethod: "Bank Transfer",
            transactionId: "TXN-001234",
            recipient: "Martinez & Associates",
            clientName: "John Smith",
            caseType: "I-130 Petition"
        },
        {
            id: 2,
            type: "client",
            description: "Maria Garcia - Employment Authorization",
            amount: 1800,
            status: "completed",
            date: "2024-01-18",
            paymentMethod: "Credit Card",
            transactionId: "TXN-001235",
            recipient: "Martinez & Associates",
            clientName: "Maria Garcia",
            caseType: "Employment Authorization"
        },
        {
            id: 3,
            type: "subscription",
            description: "Gold Plan Monthly Subscription",
            amount: 299,
            status: "completed",
            date: "2024-01-15",
            paymentMethod: "Bank Transfer",
            transactionId: "TXN-001236",
            recipient: "Platform"
        },
        {
            id: 4,
            type: "client",
            description: "Ahmed Hassan - Naturalization",
            amount: 3200,
            status: "pending",
            date: "2024-01-25",
            paymentMethod: "Check",
            transactionId: "TXN-001237",
            recipient: "Martinez & Associates",
            clientName: "Ahmed Hassan",
            caseType: "Naturalization"
        },
        {
            id: 5,
            type: "client",
            description: "Sarah Johnson - Family Reunification",
            amount: 4500,
            status: "completed",
            date: "2024-01-22",
            paymentMethod: "Wire Transfer",
            transactionId: "TXN-001238",
            recipient: "Martinez & Associates",
            clientName: "Sarah Johnson",
            caseType: "Family Reunification"
        },
        {
            id: 6,
            type: "client",
            description: "Carlos Rodriguez - Asylum Application",
            amount: 2800,
            status: "completed",
            date: "2024-01-12",
            paymentMethod: "Credit Card",
            transactionId: "TXN-001239",
            recipient: "Martinez & Associates",
            clientName: "Carlos Rodriguez",
            caseType: "Asylum Application"
        }
    ];

    const filteredPayments = payments.filter(payment => {
        const matchesSearch = payment.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
            payment.recipient.toLowerCase().includes(searchTerm.toLowerCase()) ||
            payment.transactionId.toLowerCase().includes(searchTerm.toLowerCase());
        const matchesType = filterType === "all" || payment.type === filterType;
        const matchesStatus = filterStatus === "all" || payment.status === filterStatus;
        return matchesSearch && matchesType && matchesStatus;
    });

    const getStatusBadge = (status) => {
        const statusConfig = {
            completed: { color: "bg-green-100 text-green-800", text: "Completed" },
            pending: { color: "bg-yellow-100 text-yellow-800", text: "Pending" },
            failed: { color: "bg-red-100 text-red-800", text: "Failed" },
            refunded: { color: "bg-blue-100 text-blue-800", text: "Refunded" }
        };

        const config = statusConfig[status] || statusConfig.pending;
        return (
            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${config.color}`}>
                {config.text}
            </span>
        );
    };

    const getTypeIcon = (type) => {
        const icons = {
            client: (
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                </svg>
            ),
            form: (
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
            ),
            lawyer: (
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                </svg>
            ),
            subscription: (
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
                </svg>
            )
        };
        return icons[type] || icons.client;
    };

    const handleDownloadReceipt = (paymentId) => {
    };

    const handleRefund = (paymentId) => {
    };

    const totalSpent = payments.filter(p => p.status === 'completed').reduce((sum, p) => sum + p.amount, 0);
    const pendingAmount = payments.filter(p => p.status === 'pending').reduce((sum, p) => sum + p.amount, 0);
    const failedAmount = payments.filter(p => p.status === 'failed').reduce((sum, p) => sum + p.amount, 0);

    return (
        <div>
            <div className="flex items-center justify-between mb-8">
                <div>
                    <h2 className="text-2xl font-bold text-gray-900">Payment History</h2>
                    <p className="text-gray-600 mt-1">View and manage all your payment transactions</p>
                </div>
                <motion.button
                    className="bg-blue-600 text-white px-6 py-2 rounded-lg font-medium hover:bg-blue-700 transition-colors duration-200"
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                >
                    Download All Receipts
                </motion.button>
            </div>

            {/* Summary Cards */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
                <div className="bg-green-50 rounded-lg p-4">
                    <div className="flex items-center">
                        <div className="flex-shrink-0">
                            <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                            </svg>
                        </div>
                        <div className="ml-3">
                            <p className="text-sm font-medium text-green-900">Total Spent</p>
                            <p className="text-2xl font-bold text-green-600">${totalSpent.toLocaleString()}</p>
                        </div>
                    </div>
                </div>
                <div className="bg-yellow-50 rounded-lg p-4">
                    <div className="flex items-center">
                        <div className="flex-shrink-0">
                            <svg className="w-8 h-8 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                        </div>
                        <div className="ml-3">
                            <p className="text-sm font-medium text-yellow-900">Pending</p>
                            <p className="text-2xl font-bold text-yellow-600">${pendingAmount.toLocaleString()}</p>
                        </div>
                    </div>
                </div>
                <div className="bg-red-50 rounded-lg p-4">
                    <div className="flex items-center">
                        <div className="flex-shrink-0">
                            <svg className="w-8 h-8 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                        </div>
                        <div className="ml-3">
                            <p className="text-sm font-medium text-red-900">Failed</p>
                            <p className="text-2xl font-bold text-red-600">${failedAmount.toLocaleString()}</p>
                        </div>
                    </div>
                </div>
                <div className="bg-blue-50 rounded-lg p-4">
                    <div className="flex items-center">
                        <div className="flex-shrink-0">
                            <svg className="w-8 h-8 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                            </svg>
                        </div>
                        <div className="ml-3">
                            <p className="text-sm font-medium text-blue-900">Total Transactions</p>
                            <p className="text-2xl font-bold text-blue-600">{payments.length}</p>
                        </div>
                    </div>
                </div>
            </div>

            {/* Search and Filter */}
            <div className="mb-6 flex flex-col sm:flex-row gap-4">
                <div className="flex-1">
                    <div className="relative">
                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                            <svg className="h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                            </svg>
                        </div>
                        <input
                            type="text"
                            placeholder="Search payments..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        />
                    </div>
                </div>
                <select
                    value={filterType}
                    onChange={(e) => setFilterType(e.target.value)}
                    className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                    <option value="all">All Types</option>
                    <option value="client">Client Payments</option>
                    <option value="subscription">Subscriptions</option>
                </select>
                <select
                    value={filterStatus}
                    onChange={(e) => setFilterStatus(e.target.value)}
                    className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                    <option value="all">All Status</option>
                    <option value="completed">Completed</option>
                    <option value="pending">Pending</option>
                    <option value="failed">Failed</option>
                    <option value="refunded">Refunded</option>
                </select>
            </div>

            {/* Payments List */}
            <div className="grid grid-cols-1 gap-6">
                {filteredPayments.map((payment, index) => (
                    <motion.div
                        key={payment.id}
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: index * 0.1 }}
                        className="bg-white border border-gray-200 rounded-lg p-6 hover:shadow-md transition-shadow duration-200"
                    >
                        <div className="flex flex-col h-full">
                            <div className="flex items-start justify-between mb-4">
                                <div className="flex items-center space-x-3">
                                    <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                                        <div className="text-blue-600">
                                            {getTypeIcon(payment.type)}
                                        </div>
                                    </div>
                                    <div className="flex-1">
                                        <div className="flex items-center space-x-2 mb-1">
                                            <h3 className="text-lg font-semibold text-gray-900">{payment.description}</h3>
                                            {getStatusBadge(payment.status)}
                                        </div>
                                        <p className="text-gray-600 text-sm">
                                            {payment.type === 'client' ? `Client: ${payment.clientName}` : `Paid to: ${payment.recipient}`}
                                        </p>
                                        {payment.caseType && (
                                            <p className="text-gray-500 text-xs">Case: {payment.caseType}</p>
                                        )}
                                    </div>
                                </div>
                                <div className="text-right">
                                    <div className="text-xl font-bold text-gray-900">${payment.amount}</div>
                                    <div className="text-sm text-gray-500 capitalize">{payment.type}</div>
                                </div>
                            </div>

                            <div className="flex-1 mb-4">
                                <div className="space-y-1 text-sm text-gray-500">
                                    <div className="flex justify-between">
                                        <span>Transaction ID:</span>
                                        <span className="font-mono">{payment.transactionId}</span>
                                    </div>
                                    <div className="flex justify-between">
                                        <span>Method:</span>
                                        <span>{payment.paymentMethod}</span>
                                    </div>
                                    <div className="flex justify-between">
                                        <span>Date:</span>
                                        <span>{new Date(payment.date).toLocaleDateString()}</span>
                                    </div>
                                </div>
                            </div>

                            <div className="flex items-center space-x-2 pt-4 border-t border-gray-100">
                                <motion.button
                                    onClick={() => handleDownloadReceipt(payment.id)}
                                    className="flex-1 px-3 py-2 text-sm font-medium text-blue-600 bg-blue-50 rounded-lg hover:bg-blue-100 transition-colors duration-200"
                                    whileHover={{ scale: 1.05 }}
                                    whileTap={{ scale: 0.95 }}
                                >
                                    Receipt
                                </motion.button>
                                {payment.status === 'failed' && (
                                    <motion.button
                                        onClick={() => handleRefund(payment.id)}
                                        className="flex-1 px-3 py-2 text-sm font-medium text-red-600 bg-red-50 rounded-lg hover:bg-red-100 transition-colors duration-200"
                                        whileHover={{ scale: 1.05 }}
                                        whileTap={{ scale: 0.95 }}
                                    >
                                        Refund
                                    </motion.button>
                                )}
                            </div>
                        </div>
                    </motion.div>
                ))}
            </div>

            {filteredPayments.length === 0 && (
                <div className="text-center py-12">
                    <svg className="w-16 h-16 text-gray-400 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1" />
                    </svg>
                    <h3 className="text-lg font-medium text-gray-900 mb-2">No payments found</h3>
                    <p className="text-gray-600">Try adjusting your search or filter criteria</p>
                </div>
            )}

            {/* Payment Methods */}
            <div className="mt-8 pt-6 border-t border-gray-200">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Payment Methods</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="bg-white border border-gray-200 rounded-lg p-4">
                        <div className="flex items-center justify-between">
                            <div className="flex items-center space-x-3">
                                <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                                    <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
                                    </svg>
                                </div>
                                <div>
                                    <h4 className="font-medium text-gray-900">Visa **** 1234</h4>
                                    <p className="text-sm text-gray-600">Expires 12/25</p>
                                </div>
                            </div>
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                Primary
                            </span>
                        </div>
                    </div>
                    <div className="bg-white border border-gray-200 rounded-lg p-4">
                        <div className="flex items-center justify-between">
                            <div className="flex items-center space-x-3">
                                <div className="w-10 h-10 bg-orange-100 rounded-lg flex items-center justify-center">
                                    <svg className="w-6 h-6 text-orange-600" fill="currentColor" viewBox="0 0 24 24">
                                        <path d="M7.076 21.337H2.47a.641.641 0 0 1-.633-.74L4.944.901C5.026.382 5.474 0 5.998 0h7.46c2.57 0 4.578.805 5.99 2.28a6.122 6.122 0 0 1 1.99 4.99c0 2.482-1.103 4.26-2.73 5.9a8.8 8.8 0 0 1-2.99 1.68v.09c0 .64-.32 1.23-.85 1.58l-1.12.73c-1.36.89-3.08.89-4.44 0l-1.12-.73a1.93 1.93 0 0 1-.85-1.58v-.09a8.8 8.8 0 0 1-2.99-1.68c-1.627-1.64-2.73-3.418-2.73-5.9 0-1.9.7-3.67 1.99-4.99C12.036.805 14.044 0 16.614 0h7.46c.524 0 .972.382 1.054.901l2.107 19.696a.641.641 0 0 1-.633.74h-4.606a.641.641 0 0 1-.633-.74l.633-5.92a.641.641 0 0 0-.633-.74H8.342a.641.641 0 0 0-.633.74l.633 5.92a.641.641 0 0 1-.633.74z" />
                                    </svg>
                                </div>
                                <div>
                                    <h4 className="font-medium text-gray-900">PayPal Account</h4>
                                    <p className="text-sm text-gray-600">john.doe@email.com</p>
                                </div>
                            </div>
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                                Secondary
                            </span>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default PaymentHistory;
